import React, { useState, useEffect, useRef } from "react";
import { MdClose } from "react-icons/md";
import { MdExpandMore } from "react-icons/md";
import { MdChevronLeft } from "react-icons/md";
import { MdChevronRight } from "react-icons/md";
import { useZoomNotification } from "../../../../../hooks/home/tabs/dashboard/components/useZoomNotification";
import { safeStorage } from "../../../../../utils/storage";
import "./Announcements.css";

const PAGE_SIZE = 5;
const POPUP_DISMISS_KEY = "announcement_popup_dismissed";

// 공지 고유 키 (id가 없는 경우 제목+생성일로 대체 식별)
const getNotificationKey = (item, dateObj) => {
  if (item.id) return item.id;
  const dateKey = dateObj && !isNaN(dateObj.getTime()) ? dateObj.getTime() : "";
  return `${item.title || ""}__${dateKey}`;
};

const getTodayString = () => new Date().toDateString();

const Announcements = () => {
  const { notifications, loading, error, isReady } = useZoomNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [popupOpen, setPopupOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);
  const hasCheckedPopup = useRef(false);

  // 모달 열릴 때 페이지·펼침 초기화
  useEffect(() => {
    if (modalOpen) {
      setCurrentPage(1);
      setExpandedId(null);
    }
  }, [modalOpen]);

  // 날짜 파싱 헬퍼
  const parseDate = (date) => {
    if (!date) return null;
    if (date && typeof date === "object" && date.seconds !== undefined) {
      return new Date(date.seconds * 1000);
    }
    if (date && typeof date.toDate === "function") return date.toDate();
    if (date instanceof Date) return date;
    return new Date(date);
  };

  // 짧은 날짜 (리스트용)
  const formatDate = (date) => {
    const dateObj = parseDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return "";
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    return `${month}. ${day}.`;
  };

  // 긴 날짜 (모달 상세용)
  const formatDateLong = (date) => {
    const dateObj = parseDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return "";
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    return `${year}. ${month}. ${day}.`;
  };

  // 원문에 실제 개행이 아닌 문자열 "\n"이 그대로 들어있는 경우 실제 줄바꿈으로 치환
  const normalizeDescription = (text) => {
    if (typeof text !== "string") return text;
    return text.replace(/\\r\\n|\\n/g, "\n");
  };

  // is_show가 true인 공지사항만 필터링
  const visibleNotifications = notifications.filter(
    (notification) => notification.is_show
  );

  // 가장 최신 공지 (목록은 이미 created_at 내림차순으로 정렬되어 전달됨)
  const latestNotification = visibleNotifications[0] || null;
  const latestNotificationDate = latestNotification
    ? parseDate(latestNotification.created_at)
    : null;
  const latestNotificationKey = latestNotification
    ? getNotificationKey(latestNotification, latestNotificationDate)
    : null;

  // 대시보드 진입 시 최신 공지 자동 팝업 (같은 날 "오늘 하루 보지 않기" 처리된 공지는 제외)
  useEffect(() => {
    if (hasCheckedPopup.current) return;
    if (!isReady) return;
    hasCheckedPopup.current = true;

    if (!latestNotification) return;

    let dismissed = null;
    try {
      const raw = safeStorage.getItem(POPUP_DISMISS_KEY);
      dismissed = raw ? JSON.parse(raw) : null;
    } catch (e) {
      dismissed = null;
    }

    const isDismissedToday =
      dismissed &&
      dismissed.key === latestNotificationKey &&
      dismissed.date === getTodayString();

    if (!isDismissedToday) {
      setPopupOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, latestNotification]);

  const handleClosePopup = () => {
    if (dontShowToday && latestNotificationKey) {
      safeStorage.setItem(
        POPUP_DISMISS_KEY,
        JSON.stringify({ key: latestNotificationKey, date: getTodayString() })
      );
    }
    setPopupOpen(false);
  };

  const totalPages = Math.max(1, Math.ceil(visibleNotifications.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pageItems = visibleNotifications.slice(startIndex, startIndex + PAGE_SIZE);

  const goToPage = (page) => {
    const next = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(next);
    setExpandedId(null);
  };

  if (loading) {
    return (
      <div className="dashboard-widget-card-harf">
        <div className="dashboard-card-header">
          <h3>공지 사항</h3>
        </div>
        <div>로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-widget-card-harf">
        <div className="dashboard-card-header">
          <h3>공지 사항</h3>
        </div>
        <div>공지사항을 불러올 수 없습니다. {error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-widget-card-harf">
        <div className="dashboard-card-header">
          <h3>공지 사항</h3>
          <button
            type="button"
            className="dashboard-more-link"
            onClick={() => setModalOpen(true)}
          >
            더보기
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 5L13 13M13 5L5 13"
                stroke="#696969"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform="rotate(45 9 9)"
              />
            </svg>
          </button>
        </div>
        <ul className="dashboard-announcement-list">
          {visibleNotifications.slice(0, 4).map((item, index) => (
            <li key={item.id || `announcement-${index}`}>
              <span>{item.title}</span>
              <span className="date">{formatDate(item.created_at)}</span>
            </li>
          ))}
        </ul>
      </div>

      {modalOpen && (
        <div
          className="announcements-modal-overlay"
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="announcements-modal-title"
        >
          <div
            className="announcements-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="announcements-modal-header">
              <h2 id="announcements-modal-title" className="announcements-modal-title">
                공지 사항
              </h2>
              <button
                type="button"
                className="announcements-modal-close"
                onClick={() => setModalOpen(false)}
                aria-label="닫기"
              >
                <MdClose size={24} />
              </button>
            </div>
            <div className="announcements-modal-body">
              {visibleNotifications.length === 0 ? (
                <p className="announcements-modal-empty">등록된 공지사항이 없습니다.</p>
              ) : (
                <ul className="announcements-modal-list">
                  {pageItems.map((item, idx) => {
                      const globalIndex = startIndex + idx;
                      const id = item.id || `announcement-full-${globalIndex}`;
                      const isExpanded = expandedId === id;
                      return (
                        <li
                          key={id}
                          className={`announcements-modal-item ${isExpanded ? "expanded" : ""}`}
                        >
                          <button
                            type="button"
                            className="announcements-modal-item-trigger"
                            onClick={() => setExpandedId(isExpanded ? null : id)}
                            aria-expanded={isExpanded}
                            aria-controls={`announcements-panel-${id}`}
                            id={`announcements-trigger-${id}`}
                          >
                            <span className="announcements-modal-item-title">{item.title}</span>
                            <span className="announcements-modal-item-meta">
                              {isExpanded && (
                                <span className="announcements-modal-item-date">
                                  {formatDateLong(item.created_at)}
                                </span>
                              )}
                              <MdExpandMore
                                className="announcements-modal-item-chevron"
                                size={22}
                                aria-hidden
                              />
                            </span>
                          </button>
                          <div
                            id={`announcements-panel-${id}`}
                            role="region"
                            aria-labelledby={`announcements-trigger-${id}`}
                            className="announcements-modal-item-panel"
                          >
                            <div className="announcements-modal-item-panel-inner">
                              {item.image_url && (
                                <div className="announcements-modal-item-image-wrap">
                                  <img
                                    src={item.image_url}
                                    alt=""
                                    className="announcements-modal-item-image"
                                  />
                                </div>
                              )}
                              {item.discription && (
                                <div className="announcements-modal-item-description">
                                  {normalizeDescription(item.discription)}
                                </div>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>
            {visibleNotifications.length > 0 && totalPages > 1 && (
              <div className="announcements-modal-pagination">
                <button
                  type="button"
                  className="announcements-modal-page-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="이전 페이지"
                >
                  <MdChevronLeft size={20} />
                </button>
                <span className="announcements-modal-page-info">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  className="announcements-modal-page-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="다음 페이지"
                >
                  <MdChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {popupOpen && latestNotification && (
        <div
          className="announcement-popup-overlay"
          onClick={handleClosePopup}
          role="dialog"
          aria-modal="true"
          aria-labelledby="announcement-popup-title"
        >
          <div
            className="announcement-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="announcement-popup-header">
              <span className="announcement-popup-date">
                {formatDateLong(latestNotification.created_at)}
              </span>
              <button
                type="button"
                className="announcement-popup-close"
                onClick={handleClosePopup}
                aria-label="닫기"
              >
                <MdClose size={22} />
              </button>
            </div>
            <div className="announcement-popup-body">
              <h2 id="announcement-popup-title" className="announcement-popup-title">
                {latestNotification.title}
              </h2>
              {latestNotification.image_url && (
                <div className="announcement-popup-image-wrap">
                  <img
                    src={latestNotification.image_url}
                    alt=""
                    className="announcement-popup-image"
                  />
                </div>
              )}
              {latestNotification.discription && (
                <div className="announcement-popup-description">
                  {normalizeDescription(latestNotification.discription)}
                </div>
              )}
            </div>
            <div className="announcement-popup-footer">
              <label className="announcement-popup-checkbox">
                <input
                  type="checkbox"
                  checked={dontShowToday}
                  onChange={(e) => setDontShowToday(e.target.checked)}
                />
                오늘 하루 보지 않기
              </label>
              <button
                type="button"
                className="announcement-popup-close-btn"
                onClick={handleClosePopup}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Announcements;
